import React, { useCallback, useState } from 'react';
import useAuth from '../../../hooks/useAuth';
import {
  getDashboardMetricsPreSaleEndpoint,
  getDashboardMetricsV2Endpoint,
  getDashboardPreSaleVisitsProperEndpoint,
  getDashboardVisitsProperEndpoint,
} from '../services/dashboardService';
import useAdminEntity from '../../../hooks/useAdminEntity';
import {
  PeriodMetrics,
  ProperPreSaleMetrics,
  PropersAssignedPreSaleVisits,
  PropersAssignedVisits,
  ProperV2Metrics,
} from '../models/dashboardModels';
import { Col, Container, Row } from 'reactstrap';
import useAdminPagination from '../../../hooks/useAdminPagination';
import AdminPaginator from '../../../components/admin/AdminPaginator';
import DashboardVisitManagerListTable from './DashboardVisitManagerListTable';
import DashboardPreSaleVisitManagerListTable from './DashboardPreSaleVisitManagerListTable';
import { activeTab } from '../types/dashboard.types';
import NavigationTabDashboard from './NavigationTabDashboard';
import MetricsDashboard from './MetricsDashboard';
import MetricsDateDashboard from './MetricsDateDashboard';
import useUserCountry from '../../../hooks/useUserCountry';
import {
  toStatsMetrics,
  VISIT_STATUS_MAP,
  PRE_SALE_STATUS_MAP,
} from '../adapters/dashboardAdapters';

const InternalUserDashboard: React.FC = () => {
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const { getCurrentCountry } = useUserCountry();
  const currenUserCountryCode = getCurrentCountry()?.code || '';

  const [activeTab, setActiveTab] = useState<activeTab>(
    currenUserCountryCode === 'SV' ? 'rent' : 'pre-sale',
  );

  const defaultPeriod: PeriodMetrics = { end: '', start: '' };

  // Rent metrics
  const getRentMetrics = useCallback(
    () => getDashboardMetricsV2Endpoint(token, 'internal-user', 'on_rent'),
    [token],
  );
  const [rentMetrics] = useAdminEntity<ProperV2Metrics>(
    getRentMetrics,
    console.error,
  );

  // Secondary metrics
  const getSecondaryMetrics = useCallback(
    () => getDashboardMetricsV2Endpoint(token, 'internal-user', 'on_sale'),
    [token],
  );
  const [secondaryMetrics] = useAdminEntity<ProperV2Metrics>(
    getSecondaryMetrics,
    console.error,
  );

  // Pre-sale metrics
  const getDashboardPreSaleMetrics = useCallback(
    () => getDashboardMetricsPreSaleEndpoint(token, 'internal-user'),
    [token],
  );
  const [dashboardPreSaleMetrics] = useAdminEntity<ProperPreSaleMetrics>(
    getDashboardPreSaleMetrics,
    console.error,
  );

  // Rent visits
  const [rentVisits, setRentVisits] = useState<PropersAssignedVisits[]>([]);
  const { paginator: rentPaginator, loadItems: rentLoadItems } =
    useAdminPagination(
      (queryParams: string, token: string) =>
        getDashboardVisitsProperEndpoint(
          `${queryParams.replace(/&$/, '')}&marketplace_type=on_rent`,
          token,
        ),
      setRentVisits,
      console.error,
    );

  // Secondary visits
  const [secondaryVisits, setSecondaryVisits] = useState<
    PropersAssignedVisits[]
  >([]);
  const { paginator: secondaryPaginator, loadItems: secondaryLoadItems } =
    useAdminPagination(
      (queryParams: string, token: string) =>
        getDashboardVisitsProperEndpoint(
          `${queryParams.replace(/&$/, '')}&marketplace_type=on_sale`,
          token,
        ),
      setSecondaryVisits,
      console.error,
    );

  // Pre-sale visits
  const [dashboardPreSaleVisits, setDashboardPreSaleVisits] = useState<
    PropersAssignedPreSaleVisits[]
  >([]);
  const {
    paginator: paginatorPreSaleVisits,
    loadItems: dashboardPreSaleVisitLoadItems,
  } = useAdminPagination(
    getDashboardPreSaleVisitsProperEndpoint,
    setDashboardPreSaleVisits,
    console.error,
  );

  if (!rentMetrics || !secondaryMetrics || !dashboardPreSaleMetrics) {
    return <>Cargando</>;
  }

  const rentInfo = toStatsMetrics(rentMetrics.visits, VISIT_STATUS_MAP);
  const secondaryInfo = toStatsMetrics(
    secondaryMetrics.visits,
    VISIT_STATUS_MAP,
  );
  const preSaleInfo = toStatsMetrics(
    dashboardPreSaleMetrics.visits,
    PRE_SALE_STATUS_MAP,
  );

  const activeStats =
    activeTab === 'rent'
      ? rentInfo
      : activeTab === 'secondary'
        ? secondaryInfo
        : preSaleInfo;

  const activePeriod =
    activeTab === 'rent'
      ? (rentMetrics?.period ?? defaultPeriod)
      : activeTab === 'secondary'
        ? (secondaryMetrics?.period ?? defaultPeriod)
        : (dashboardPreSaleMetrics?.period ?? defaultPeriod);

  return (
    <Container className="mt-6" fluid>
      <NavigationTabDashboard
        activeTab={activeTab}
        onTabChange={setActiveTab}
        preSaleCount={preSaleInfo.total}
        rentCount={rentInfo.total}
        secondaryCount={secondaryInfo.total}
      />

      <MetricsDateDashboard activeTab={activeTab} period={activePeriod} />

      <MetricsDashboard activeTab={activeTab} stats={activeStats} />

      {activeTab === 'rent' && (
        <Row>
          <Col sm={12}>
            {rentVisits && rentVisits.length > 0 && (
              <DashboardVisitManagerListTable
                visits={rentVisits}
                footer={
                  <AdminPaginator
                    page={rentPaginator.page}
                    totalPages={rentPaginator.pageCount}
                    onPageChange={rentLoadItems}
                  />
                }
              />
            )}
          </Col>
        </Row>
      )}

      {activeTab === 'secondary' && (
        <Row>
          <Col sm={12}>
            {secondaryVisits && secondaryVisits.length > 0 && (
              <DashboardVisitManagerListTable
                visits={secondaryVisits}
                footer={
                  <AdminPaginator
                    page={secondaryPaginator.page}
                    totalPages={secondaryPaginator.pageCount}
                    onPageChange={secondaryLoadItems}
                  />
                }
              />
            )}
          </Col>
        </Row>
      )}

      {activeTab === 'pre-sale' && (
        <Row>
          <Col sm={12}>
            {dashboardPreSaleVisits && dashboardPreSaleVisits.length > 0 && (
              <DashboardPreSaleVisitManagerListTable
                visits={dashboardPreSaleVisits}
                footer={
                  <AdminPaginator
                    page={paginatorPreSaleVisits.page}
                    totalPages={paginatorPreSaleVisits.pageCount}
                    onPageChange={dashboardPreSaleVisitLoadItems}
                  />
                }
              />
            )}
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default InternalUserDashboard;
